import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchIncomingRequests,
  fetchOutgoingRequests,
  fetchApprovedAccess,
  fetchGrantedAccess,
  requestPanelAccess,
  grantPanelAccess,
  approvePanelRequest,
  rejectPanelRequest,
  revokePanelAccess,
} from '../../store/slices/panelAccessSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { PlusIcon, CloseIcon, ChevronDownIcon } from '../chat/SidebarIcons';
import { fetchUsers } from '../../store/slices/userSlice';
import PresignedAvatar from '../common/PresignedAvatar';

const userDisplayName = (user) => {
  if (!user) return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.username || 'Someone';
};

const initialsFromName = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`;
  return (parts[0] || 'U').slice(0, 2);
};

const MetricsIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const SharedPanelsWidget = ({
  onSelectUser,
  currentViewingUserId,
  onHide,
  onShowMetrics,
  metricsActive = false,
  variant = 'card',
}) => {
  const dispatch = useDispatch();
  const { incomingRequests, outgoingRequests, approvedAccess, grantedAccess } = useSelector(
    (state) => state.panelAccess
  );
  const currentUser = useSelector((state) => state.auth.user);
  const reduxUsers = useSelector((state) => state.users.users || []);

  const [popupOpen, setPopupOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [requestingId, setRequestingId] = useState(null);
  const [grantingId, setGrantingId] = useState(null);

  useEffect(() => {
    dispatch(fetchIncomingRequests());
    dispatch(fetchOutgoingRequests());
    dispatch(fetchApprovedAccess());
    dispatch(fetchGrantedAccess());
    dispatch(fetchUsers());
  }, [dispatch]);

  const pendingIncoming = incomingRequests.filter((r) => r.status === 'PENDING');
  const pendingOutgoing = outgoingRequests.filter((r) => r.status === 'PENDING');
  const approvedPanelAccess = approvedAccess.filter((acc) => acc.status === 'APPROVED');

  const hadAccessRef = useRef(false);

  useEffect(() => {
    if (!currentViewingUserId) {
      hadAccessRef.current = false;
      return;
    }
    const hasAccess = approvedPanelAccess.some((acc) => acc.targetUserId === currentViewingUserId);
    if (hasAccess) {
      hadAccessRef.current = true;
      return;
    }
    if (hadAccessRef.current) {
      onSelectUser(null);
    }
  }, [approvedPanelAccess, currentViewingUserId, onSelectUser]);

  const ownName = userDisplayName(currentUser) || 'My';
  const viewingAccess = approvedPanelAccess.find((acc) => acc.targetUserId === currentViewingUserId);
  const titleName = viewingAccess?.targetUserName || ownName;

  const canGrantAccessTo = (userId) => {
    if (!userId || userId === currentUser?.id) return false;
    if (grantedAccess.some((a) => a.requesterId === userId)) return false;
    if (approvedPanelAccess.some((a) => a.targetUserId === userId)) return false;
    return true;
  };

  const handleViewUser = (userId) => {
    onSelectUser(userId === currentUser?.id ? null : userId);
    setPopupOpen(false);
    setSearch('');
  };

  const handleRequestAccess = async (userId) => {
    if (!userId) return;
    setRequestingId(userId);
    try {
      await dispatch(requestPanelAccess(userId)).unwrap();
      dispatch(addNotification({ type: 'success', message: 'Access request sent' }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.error || err?.message || 'Failed to send request',
      }));
    } finally {
      setRequestingId(null);
    }
  };

  const handleGrantAccess = async (userId) => {
    if (!userId) return;
    setGrantingId(userId);
    try {
      await dispatch(grantPanelAccess(userId)).unwrap();
      dispatch(addNotification({ type: 'success', message: 'Access granted' }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.error || err?.message || 'Failed to grant access',
      }));
    } finally {
      setGrantingId(null);
    }
  };

  const query = search.trim().toLowerCase();
  const isSearching = query.length > 0;
  const people = useMemo(() => {
    if (!isSearching) return [];
    return reduxUsers
      .filter((u) => u.id !== currentUser?.id)
      .filter((u) => {
        const name = userDisplayName(u).toLowerCase();
        const username = (u.username || '').toLowerCase();
        return name.includes(query) || username.includes(query);
      });
  }, [reduxUsers, currentUser?.id, query, isSearching]);

  const accessiblePeople = useMemo(() => {
    return approvedPanelAccess.map((acc) => {
      const user = reduxUsers.find((u) => u.id === acc.targetUserId);
      const name = user ? userDisplayName(user) : (acc.targetUserName || 'Someone');
      return {
        id: acc.targetUserId,
        name,
        username: user?.username,
        profilePicture: user?.profilePicture,
      };
    });
  }, [approvedPanelAccess, reduxUsers]);

  const personStatus = (userId) => {
    if (approvedPanelAccess.some((a) => a.targetUserId === userId)) return 'approved';
    if (pendingOutgoing.some((r) => r.targetUserId === userId)) return 'requested';
    return 'none';
  };

  const popup = popupOpen && ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-orange-200/80 dark:border-gray-700 flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="View others' workload"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white">
              Whose workloads do you want to see?
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Open a panel you already have access to, or send an access request.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPopupOpen(false)}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
            Users whose access you already have
          </h4>
          {accessiblePeople.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              You don’t have access to anyone else’s workload yet.
            </p>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-1 -mx-1 px-1">
              {accessiblePeople.map((person) => {
                const selected = currentViewingUserId === person.id;
                return (
                  <button
                    key={person.id}
                    type="button"
                    onClick={() => handleViewUser(person.id)}
                    className="flex flex-col items-center gap-1.5 min-w-[4.5rem] max-w-[4.5rem] flex-shrink-0 group"
                    title={`View ${person.name}`}
                  >
                    <span
                      className={`flex h-14 w-14 items-center justify-center rounded-full p-[2px] ${
                        selected
                          ? 'bg-orange-500'
                          : 'bg-orange-200 group-hover:bg-orange-400'
                      }`}
                    >
                      <PresignedAvatar
                        fileKey={person.profilePicture}
                        cacheKey={person.username}
                        name={person.name}
                        initials={initialsFromName(person.name)}
                        className="h-full w-full rounded-full object-cover"
                        fallbackClassName="flex h-full w-full items-center justify-center rounded-full bg-orange-500 text-white text-sm font-bold"
                      />
                    </span>
                    <span className={`text-[11px] font-semibold leading-tight text-center truncate w-full ${
                      selected ? 'text-orange-600 dark:text-orange-400' : 'text-gray-700 dark:text-gray-300'
                    }`}
                    >
                      {person.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search to find someone…"
            className="w-full text-sm rounded-xl border border-orange-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 outline-none focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
          {pendingIncoming.length > 0 && (
            <section>
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-orange-600 mb-2">
                Incoming requests
              </h4>
              <div className="space-y-2">
                {pendingIncoming.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between gap-2 rounded-xl border border-orange-100 dark:border-orange-900/40 bg-orange-50/70 dark:bg-orange-950/20 px-3 py-2"
                  >
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 min-w-0 truncate">
                      {req.requesterName}
                    </span>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => dispatch(approvePanelRequest(req.id))}
                        className="px-2 py-1 text-[11px] font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => dispatch(rejectPanelRequest(req.id))}
                        className="px-2 py-1 text-[11px] font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {isSearching ? (
            <>
          <section>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Search results
            </h4>
            {people.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                No people match that search.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {people.map((user) => {
                  const status = personStatus(user.id);
                  const name = userDisplayName(user);
                  return (
                    <li
                      key={user.id}
                      className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white dark:bg-gray-800"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{name}</p>
                        {user.username ? (
                          <p className="text-[11px] text-gray-500 truncate">@{user.username}</p>
                        ) : null}
                      </div>
                      {status === 'approved' ? (
                        <button
                          type="button"
                          onClick={() => handleViewUser(user.id)}
                          className="px-3 py-1.5 text-xs font-bold rounded-lg bg-orange-500 text-white hover:bg-orange-600 flex-shrink-0"
                        >
                          View
                        </button>
                      ) : status === 'requested' ? (
                        <span className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 flex-shrink-0">
                          Requested
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRequestAccess(user.id)}
                          disabled={requestingId === user.id}
                          className="px-3 py-1.5 text-xs font-bold rounded-lg border-2 border-orange-400 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/30 disabled:opacity-50 flex-shrink-0"
                        >
                          {requestingId === user.id ? 'Sending…' : 'Request access'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Share my workload
            </h4>
            <ul className="space-y-1.5">
              {reduxUsers
                .filter((u) => canGrantAccessTo(u.id))
                .filter((u) => userDisplayName(u).toLowerCase().includes(query))
                .slice(0, 8)
                .map((user) => (
                  <li key={`grant-${user.id}`} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                      {userDisplayName(user)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleGrantAccess(user.id)}
                      disabled={grantingId === user.id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      <PlusIcon className="w-3 h-3" />
                      {grantingId === user.id ? 'Granting…' : 'Grant'}
                    </button>
                  </li>
                ))}
            </ul>
          </section>
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
              Search for someone to view their workload or send an access request.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );

  if (variant === 'inline') {
    return (
      <>
        <div className="flex items-center gap-1 min-w-0 w-full px-0.5">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-snug truncate min-w-0">
            {titleName} Workloads
          </h3>
          <button
            type="button"
            onClick={() => setPopupOpen(true)}
            className="relative inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10 flex-shrink-0"
            aria-label="View others' workload"
            title="View others' workload"
          >
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${popupOpen ? 'rotate-180' : ''}`} />
            {pendingIncoming.length > 0 && (
              <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-orange-500" />
            )}
          </button>
          <div className="ml-auto flex items-center gap-0.5 flex-shrink-0">
            {viewingAccess && (
              <button
                type="button"
                onClick={() => onSelectUser(null)}
                className="text-[11px] font-semibold text-orange-600 hover:underline px-1.5"
              >
                Mine
              </button>
            )}
            {onShowMetrics && (
              <button
                type="button"
                onClick={onShowMetrics}
                className={`inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors ${
                  metricsActive
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'
                }`}
                aria-label={`View ${titleName} chart and task status`}
                title="Chart and task status"
              >
                <MetricsIcon />
              </button>
            )}
          </div>
        </div>
        {popup}
      </>
    );
  }

  return (
    <div className="mb-3 rounded-2xl border-2 border-orange-200 dark:border-orange-900/40 bg-white dark:bg-gray-800 overflow-hidden shadow-sm">
      <div className="px-3.5 pt-3 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-orange-500">Workloads</p>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-snug truncate">
              {titleName} Workloads
            </h3>
            {viewingAccess ? (
              <button
                type="button"
                onClick={() => onSelectUser(null)}
                className="mt-1 text-[11px] font-semibold text-orange-600 hover:underline"
              >
                Back to my workload
              </button>
            ) : (
              <p className="mt-0.5 text-[11px] text-gray-500">Your tasks, reminders, and workflows</p>
            )}
          </div>
          {onHide && (
            <button
              type="button"
              onClick={onHide}
              className="text-[11px] font-medium text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex-shrink-0"
            >
              Hide
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setPopupOpen(true)}
          className="mt-3 w-full rounded-xl border-2 border-orange-400 bg-white hover:bg-orange-50 dark:hover:bg-orange-950/20 px-3 py-2.5 text-left transition-colors"
        >
          <span className="block text-sm font-bold text-orange-600 dark:text-orange-400">
            View others&apos; workload
          </span>
          <span className="block text-[11px] text-gray-500 mt-0.5">
            Choose whose panel to open, or request access
          </span>
        </button>

        {pendingIncoming.length > 0 && (
          <button
            type="button"
            onClick={() => setPopupOpen(true)}
            className="mt-2 w-full text-left text-[11px] font-semibold text-orange-700 dark:text-orange-300"
          >
            {pendingIncoming.length} incoming request{pendingIncoming.length === 1 ? '' : 's'} — review
          </button>
        )}
      </div>
      {popup}
    </div>
  );
};

export default SharedPanelsWidget;
